import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, MapPin, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { CartEventItem } from "./CartEventItem";
import { CartProductItem } from "./CartProductItem";
import { LightningBolt } from "@/components/icons/LightningBolt";

export function CartDrawer() {
  const navigate = useNavigate();
  const {
    eventItems,
    productItems,
    itemCount,
    total,
    isOpen,
    closeCart,
    updateQuantity,
    removeItem,
  } = useCart();

  const handleCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  const handleViewEvents = () => {
    closeCart();
    navigate("/events");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-50 bg-black/80"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-card border-l border-border flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <LightningBolt size={20} filled className="text-primary" />
                <h3 className="text-xl font-heading font-bold">MI RESERVA</h3>
              </div>
              <button
                onClick={closeCart}
                className="p-2 rounded-lg hover:bg-secondary transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {itemCount === 0 ? (
                <div className="text-center py-16">
                  <LightningBolt
                    size={48}
                    className="mx-auto text-muted-foreground mb-4"
                  />
                  <p className="text-lg font-medium mb-2">
                    Todavía no reservaste nada
                  </p>
                  <p className="text-muted-foreground mb-6">
                    Explorá los próximos eventos y sumá tu lugar.
                  </p>
                  <button
                    onClick={handleViewEvents}
                    className="btn-lightning px-6"
                  >
                    VER PRÓXIMOS EVENTOS
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Events Section */}
                  {eventItems.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Experiencias
                      </h4>
                      <div className="space-y-3">
                        {eventItems.map((item) => (
                          <CartEventItem
                            key={item.id}
                            item={item}
                            onRemove={() => removeItem(item.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Products Section */}
                  {productItems.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Merch
                      </h4>
                      <div className="space-y-3">
                        {productItems.map((item) => (
                          <CartProductItem
                            key={item.id}
                            item={item}
                            onUpdateQuantity={(qty) =>
                              updateQuantity(item.id, qty)
                            }
                            onRemove={() => removeItem(item.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {itemCount > 0 && (
              <div className="p-4 border-t border-border space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total</span>
                  <span className="text-2xl font-heading font-bold text-primary">
                    ${total.toLocaleString("es-AR")}
                  </span>
                </div>
                <button onClick={handleCheckout} className="w-full btn-lightning">
                  {eventItems.length > 0 ? "CONFIRMAR MI LUGAR ⚡️" : "FINALIZAR COMPRA ⚡️"}
                </button>
                <button
                  onClick={handleViewEvents}
                  className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Seguir viendo fechas
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
